import { FC, Fragment, useState } from "react";
import { createPortal } from "react-dom";
import { ChangeNames } from "../../clients/FirebaseClient/types";
import { useFormModel } from "../core/FormModelHelpes";
import { NumberInput } from "../core/NumberField";
import { useStyles } from "../../useStyles";
import { Switch } from "../core/Switch";
import { Box, Divider, InputLabel } from "@material-ui/core";

import { ChargingDeviceIcon, CloseIcon } from "../core/icons/Icons";
import { BoxedInputWithIconAndOptionalSwitch } from "../core/BoxedInputWithIconAndOptionalSwitch";
import ChargerManagementStaticExample from "../../assets/images/charger_load_management_static_example.svg";
import ChargerManagementDynamicExample from "../../assets/images/charger_load_management_dynamic_example.svg";

function ModalImageContent(props: { onClose: () => void; imagePath: string; title: string }) {
    const { onClose, imagePath, title } = props;
    return (
        <Box
            id="modalbackground"
            sx={{
                position: "fixed",
                zIndex: 1000,
                left: 0,
                top: 0,
                width: "100vw",
                height: "100%",
                overflow: "hidden",
                bgcolor: "rgba(0,0,0,0.6)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
            }}
        >
            <Box
                id="modalContent"
                sx={{
                    bgcolor: "#fefefe",
                    margin: { md: "25% auto" },
                    border: "1px solid #888",
                    width: { xs: "100%", md: "fit-content" },
                    height: { xs: "100%", sm: "100%", md: "auto" },
                }}
            >
                <Box
                    sx={{ flexDirection: "row", display: "flex", justifyContent: "space-between" }}
                >
                    <h3
                        style={{
                            marginTop: "15.5px",
                            paddingTop: "6px",
                            marginLeft: "32px",
                            color: "#383634",
                        }}
                    >
                        {title}
                    </h3>
                    <Box onClick={onClose} sx={{ margin: "15.5px 24px" }}>
                        <CloseIcon size="size32" />
                    </Box>
                </Box>
                <Divider />
                <Box
                    sx={{
                        display: "flex",
                        height: { xs: "90%", md: "auto" },
                        width: { xs: "100%", md: "auto" },
                        flexDirection: { xs: "column", md: "unset" },
                        justifyContent: "center",
                    }}
                >
                    <Box
                        id="modalZoomableImage"
                        sx={{
                            margin: { sm: "10px" },
                            display: "flex",
                            justifyContent: "center",
                            width: { md: "85vw" },
                        }}
                    >
                        <img src={imagePath} alt="" style={{ width: "100%" }} />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export const ChargerLoadManagementForm: FC = () => {
    const classes = useStyles();
    const { values, getFieldProps, setValues } =
        useFormModel<ChangeNames.CHARGER_LOAD_MANAGEMENT>();
    const [showStaticImageModel, setShowStaticImageModel] = useState(false);
    const [showDynamicImageModel, setShowDynamicImageModel] = useState(false);
    return (
        <Fragment>
            <h3 className={classes.h3} style={{ marginTop: 28, marginBottom: 16 }}>
                Lastmanagementsystem
            </h3>
            <p className={classes.p} style={{ marginBottom: 32 }}>
                Ist ein Lastmanagementsystem zur Begrenzung und Lastverteilung der Ladeleistung
                geplant?
            </p>
            <BoxedInputWithIconAndOptionalSwitch
                icon={<ChargingDeviceIcon />}
                content={
                    <Fragment>
                        <p className={classes.label} style={{ marginBottom: 8 }}>
                            Lastmanagement statisch
                        </p>
                        <p className={classes.p} style={{ marginBottom: 24 }}>
                            Beim statischen Lastmanagementsystem wird ein maximaler Leistungswert
                            für alle integrierten Ladepunkte vorgegeben, der nicht überschritten
                            werden darf. Die verfügbare Leistung wird dann über eine intelligente
                            Steuerung auf alle Ladepunkte verteilt. Es ist dabei möglich, je nach
                            eingesetztem Produkt unterschiedliche Priorisierungen der Ladepunkte
                            vorzunehmen.
                        </p>
                    </Fragment>
                }
                footerContent={
                    <>
                        <img
                            src={ChargerManagementStaticExample}
                            style={{ cursor: "zoom-in" }}
                            onClick={() => {
                                setShowStaticImageModel(true);
                            }}
                            alt=""
                            width={"100%"}
                        />
                        {showStaticImageModel &&
                            createPortal(
                                <ModalImageContent
                                    title="Lastmanagement statisch"
                                    imagePath={ChargerManagementStaticExample}
                                    onClose={() => setShowStaticImageModel(false)}
                                />,
                                document.body,
                            )}
                    </>
                }
                useSwitch={
                    <Switch
                        checked={values.chargerLoadManagement.value.loadManagement.static}
                        {...getFieldProps("chargerLoadManagement.value.loadManagement.static")}
                        onChange={(e, checked) =>
                            setValues((values) => {
                                values.chargerLoadManagement.value.loadManagement = {
                                    static: checked,
                                    dynamic: false,
                                };
                            })
                        }
                    />
                }
                detailCondition={values.chargerLoadManagement.value.loadManagement.static}
                detail={
                    <Fragment>
                        <InputLabel className={classes.labledTextfieldLabel}>
                            Begrenzte maximale Leistung
                        </InputLabel>
                        <NumberInput
                            className={classes.textField}
                            fixedDecimalScale={false}
                            allowLeadingZeros={false}
                            decimalScale={values.chargerLoadManagement.value.demand.precision}
                            isNumericString={false}
                            allowNegative={false}
                            thousandSeparator="."
                            decimalSeparator=","
                            adornment={values.chargerLoadManagement.value.demand.unit}
                            value={values.chargerLoadManagement.value.demand.amount || ""}
                            placeholder="0"
                            onValueChange={(v) =>
                                setValues((values) => {
                                    values.chargerLoadManagement.value.demand.amount =
                                        v.floatValue || null;
                                })
                            }
                            error={values.chargerLoadManagement.error != null}
                        />
                    </Fragment>
                }
            />
            <BoxedInputWithIconAndOptionalSwitch
                icon={<ChargingDeviceIcon />}
                content={
                    <Fragment>
                        <p className={classes.label} style={{ marginBottom: 8 }}>
                            Lastmanagement dynamisch
                        </p>
                        <p className={classes.p} style={{ marginBottom: 24 }}>
                            Bei einem dynamischen Lastmanagementsystem wird ein maximaler Leistungswert
                            für alle integrierten Ladepunkte vorgegeben und zusätzlich der aktuelle
                            Stromverbrauch am Netzanschluss gemessen. Das System weiß dadurch,
                            welche Leistung im Moment für das gesamte Objekt benötigt wird und
                            verteilt die übrige Leistung mittels einer intelligenten Steuerung auf
                            die Ladepunkte. Es ist dabei möglich, je nach eingesetztem Produkt
                            unterschiedliche Priorisierungen der Ladepunkte vorzunehmen.
                        </p>
                    </Fragment>
                }
                footerContent={
                    <>
                        <img
                            src={ChargerManagementDynamicExample}
                            style={{ cursor: "zoom-in" }}
                            onClick={() => {
                                setShowDynamicImageModel(true);
                            }}
                            alt=""
                            width={"100%"}
                        />
                        {showDynamicImageModel &&
                            createPortal(
                                <ModalImageContent
                                    title="Lastmanagement dynamisch"
                                    imagePath={ChargerManagementDynamicExample}
                                    onClose={() => setShowDynamicImageModel(false)}
                                />,
                                document.body,
                            )}
                    </>
                }
                useSwitch={
                    <Switch
                        checked={values.chargerLoadManagement.value.loadManagement.dynamic}
                        {...getFieldProps("chargerLoadManagement.value.loadManagement.dynamic")}
                        onChange={(e, checked) =>
                            setValues((values) => {
                                values.chargerLoadManagement.value.loadManagement = {
                                    static: false,
                                    dynamic: checked,
                                };
                            })
                        }
                    />
                }
                detailCondition={values.chargerLoadManagement.value.loadManagement.dynamic}
                detail={
                    <Fragment>
                        <InputLabel className={classes.labledTextfieldLabel}>
                            Maximal mögliche Leistung am Netzanschluss
                        </InputLabel>
                        <NumberInput
                            className={classes.textField}
                            fixedDecimalScale={false}
                            allowLeadingZeros={false}
                            decimalScale={values.chargerLoadManagement.value.demand.precision}
                            isNumericString={false}
                            allowNegative={false}
                            thousandSeparator="."
                            decimalSeparator=","
                            adornment={values.chargerLoadManagement.value.demand.unit}
                            value={values.chargerLoadManagement.value.demand.amount || ""}
                            placeholder="0"
                            onValueChange={(v) =>
                                setValues((values) => {
                                    values.chargerLoadManagement.value.demand.amount =
                                        v.floatValue || null;
                                })
                            }
                            error={values.chargerLoadManagement.error != null}
                        />
                    </Fragment>
                }
            />
        </Fragment>
    );
};
